import React from "react";

import { Grid, Skeleton, Stack, useTheme, alpha } from "@mui/material";

const DashboardSkeleton = () => {
  const theme = useTheme();

  const cardSx = {
    borderRadius: 2,
    border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
    p: 2,
    bgcolor: "background.paper",
  };

  return (
    <Stack spacing={3}>
      {/* Page header skeleton */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack spacing={1}>
          <Skeleton variant="text" width={120} height={28} />
          <Skeleton variant="text" width={280} height={16} />
        </Stack>
        <Stack direction="row" spacing={1}>
          <Skeleton
            variant="rounded"
            width={130}
            height={34}
            sx={{ borderRadius: "8px" }}
          />
          <Skeleton
            variant="rounded"
            width={110}
            height={34}
            sx={{ borderRadius: "8px" }}
          />
        </Stack>
      </Stack>

      {/* KPI grid skeleton */}
      <Grid container spacing={2}>
        {Array.from({ length: 4 }).map((_, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Stack sx={cardSx} spacing={1}>
              <Skeleton variant="text" width={60} height={28} />
              <Skeleton variant="text" width={80} height={14} />
              <Skeleton variant="text" width={100} height={12} />
            </Stack>
          </Grid>
        ))}
      </Grid>

      {/* 2-column card grid skeleton */}
      <Grid container spacing={2}>
        {/* Left column: Health summary + Recent deployments */}
        <Grid item xs={12} md={6}>
          <Stack spacing={2}>
            <Stack sx={cardSx} spacing={1.5}>
              <Skeleton variant="text" width={140} height={16} />
              <Grid container spacing={1.5}>
                {Array.from({ length: 3 }).map((_, i) => (
                  <Grid item xs={4} key={i}>
                    <Stack spacing={1}>
                      <Skeleton variant="text" width={50} height={12} />
                      <Skeleton variant="text" width={40} height={22} />
                      <Skeleton
                        variant="rounded"
                        width="100%"
                        height={6}
                        sx={{ borderRadius: 3 }}
                      />
                    </Stack>
                  </Grid>
                ))}
              </Grid>
            </Stack>
            <Stack sx={cardSx} spacing={1}>
              <Skeleton variant="text" width={160} height={16} />
              {Array.from({ length: 4 }).map((_, i) => (
                <Stack
                  key={i}
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{
                    p: 1.5,
                    border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
                    borderRadius: 1,
                  }}
                >
                  <Skeleton variant="circular" width={8} height={8} />
                  <Stack spacing={0.5} sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="55%" height={14} />
                    <Skeleton variant="text" width="40%" height={12} />
                  </Stack>
                  <Skeleton
                    variant="rounded"
                    width={50}
                    height={24}
                    sx={{ borderRadius: 1 }}
                  />
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Grid>

        {/* Right column: Quick actions + Recent projects + Activity feed */}
        <Grid item xs={12} md={6}>
          <Stack spacing={2}>
            <Stack sx={cardSx} spacing={1}>
              <Skeleton variant="text" width={120} height={16} />
              <Grid container spacing={1.5}>
                {Array.from({ length: 2 }).map((_, i) => (
                  <Grid item xs={6} key={i}>
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      sx={{
                        p: 1.75,
                        border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
                        borderRadius: 1,
                      }}
                    >
                      <Skeleton variant="circular" width={32} height={32} />
                      <Stack spacing={0.5} sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="70%" height={14} />
                        <Skeleton variant="text" width="90%" height={12} />
                      </Stack>
                    </Stack>
                  </Grid>
                ))}
              </Grid>
            </Stack>
            <Stack sx={cardSx} spacing={1}>
              <Skeleton variant="text" width={140} height={16} />
              {Array.from({ length: 3 }).map((_, i) => (
                <Stack
                  key={i}
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{
                    p: 1.75,
                    border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
                    borderRadius: 1,
                  }}
                >
                  <Skeleton variant="circular" width={32} height={32} />
                  <Stack spacing={0.5} sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="60%" height={14} />
                    <Skeleton variant="text" width="80%" height={12} />
                  </Stack>
                  <Skeleton variant="text" width={12} height={14} />
                </Stack>
              ))}
            </Stack>
            <Stack sx={cardSx} spacing={1}>
              <Skeleton variant="text" width={120} height={16} />
              {Array.from({ length: 4 }).map((_, i) => (
                <Stack
                  key={i}
                  direction="row"
                  spacing={1.5}
                  alignItems="flex-start"
                  sx={{
                    py: 1.25,
                    borderBottom:
                      i < 3
                        ? `1px solid ${alpha(theme.palette.divider, 0.06)}`
                        : "none",
                  }}
                >
                  <Skeleton
                    variant="circular"
                    width={8}
                    height={8}
                    sx={{ mt: 0.5 }}
                  />
                  <Stack spacing={0.5} sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="75%" height={14} />
                    <Skeleton variant="text" width={50} height={12} />
                  </Stack>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default DashboardSkeleton;
